import axios from "axios";
import { MAP_SERVICE_ADDRESS } from "constants/app.constants";

const wellDataEndpoint = process.env.REACT_APP_WELL_DETAIL_SERVICE;

export async function fetchWellDetail({
  uwi,
  isFacility,
  fields,
  defaultSignificantFigures,
  useStrictRounding,
  timeout,
  signal
}: {
  uwi: string;
  isFacility: boolean;
  fields: string[];
  defaultSignificantFigures: number;
  useStrictRounding: boolean;
  /**
   * Milliseconds to wait before giving up. There is no global axios timeout,
   * so a caller that asks for many fields at once sets its own — a request the
   * service never answers otherwise leaves the caller loading for ever. Left
   * unset by the map tooltip, whose handful of fields always answer quickly.
   */
  timeout?: number;
  /** Drops the request when the caller stops needing the answer. */
  signal?: AbortSignal;
}) {
  // Stick ids embed a UWI ("<scenario>_00/14-21-069-05W6/MONT"); an unencoded
  // slash splits the path and the request matches no route.
  const response = await axios.get(`${wellDataEndpoint}/${encodeURIComponent(uwi)}`, {
    params: {
      entityKind: isFacility ? "Facility" : "Well",
      fields: fields,
      defaultSignificantFigures,
      useStrictRounding
    },
    paramsSerializer: {
      indexes: null
    },
    timeout,
    signal
  });
  return response;
}

/**
 * Fetch aggregated field values for a group of wells.
 * Used by the map tooltip when hovering a group entity in group mode.
 *
 * Calls the map-svc group-detail endpoint which resolves the bin's member
 * UWIs from the cached map layer (via lookup IDs), fetches aggregated data
 * from data-svc, and replaces Header.EntityName with the group display name.
 */
export async function fetchGroupDetail({
  txnId,
  uwi,
  fields,
  defaultSignificantFigures,
  useStrictRounding,
  groupByProperty,
  groupByTitle
}: {
  txnId: string;
  uwi: string;
  fields: string[];
  defaultSignificantFigures: number;
  useStrictRounding: boolean;
  groupByProperty?: string;
  groupByTitle?: string;
}) {
  const response = await axios.post(`${MAP_SERVICE_ADDRESS}/map/${txnId}/group-detail`, {
    uwi,
    fields,
    defaultSignificantFigures,
    useStrictRounding,
    groupByProperty,
    groupByTitle
  });
  return response;
}
